import axios from "axios";
import { createAdvertiser } from "./createAdvertiser";
import { getAdvertiserData } from "./getAdvertiserData";
import { getAdvertiserDetails } from "./getAdvertiserDetails";
import { createAdvertiserCategory } from "./createAdvertiserCategory";
import { deleteAdvertiserCategory } from "./deleteAdvertiserCategory";

/**
 * Register a new advertiser with optional SlotCost
 * @param {string} fname - First name
 * @param {string} lname - Last name
 * @param {string} email - Email address
 * @param {string} companyName - Company name
 * @param {string} password - Password
 * @param {string} categoryId - Category ID
 * @param {number} slotCost - Optional slot cost (decimal, min 0)
 * @param {string} token - Authorization token
 * @returns {Promise} API response
 */
export const registerAdvertiser = (fname, lname, email, companyName, password, categoryId, slotCost, token) => {
  const payload = {
    fname,
    lname,
    email,
    companyName,
    password,
    categoryId,
  };

  if (slotCost !== undefined && slotCost !== null && slotCost !== "") {
    payload.slotCost = parseFloat(slotCost);
  }

  return createAdvertiser(payload, token);
};

/**
 * Get list of all advertisers
 * @param {string} token - Authorization token
 * @param {number} page - Optional page number (unused by backend)
 * @param {number} pageSize - Optional page size (unused by backend)
 * @returns {Promise} API response with advertisers list
 */
export const getAdvertisers = (token, page = null, pageSize = null) => {
  return getAdvertiserData(token, page, pageSize);
};

/**
 * Get advertiser details by ID
 * @param {string} token - Authorization token
 * @param {string} advertiserId - Advertiser ID
 * @returns {Promise} API response with advertiser data and campaigns
 */
export const getAdvertiserDetailsById = (token, advertiserId) => {
  return getAdvertiserDetails(token, advertiserId);
};

/**
 * Update advertiser data (partial update)
 * @param {string} advertiserId - Advertiser ID (required)
 * @param {Object} updates - Object containing fields to update: {fname?, lname?, companyName?, email?, categoryId?, slotCost?, active?, password?}
 * @param {string} token - Authorization token
 * @returns {Promise} API response
 */
export const updateAdvertiser = (advertiserId, updates, token) => {
  if (!advertiserId) {
    return Promise.reject(new Error("Advertiser ID is required"));
  }

  // Build payload with only non-undefined fields + advertiserId
  const payload = { advertiserId };

  if (updates.fname !== undefined) payload.fname = updates.fname;
  if (updates.lname !== undefined) payload.lname = updates.lname;
  if (updates.companyName !== undefined) payload.companyName = updates.companyName;
  if (updates.email !== undefined) payload.email = updates.email;
  if (updates.categoryId !== undefined) payload.categoryId = updates.categoryId;
  if (updates.slotCost !== undefined) payload.slotCost = parseFloat(updates.slotCost);
  if (updates.active !== undefined) payload.active = updates.active;
  if (updates.password !== undefined && updates.password !== null && updates.password !== "") {
    payload.password = updates.password;
  }

  return axios.post(process.env.REACT_APP_API_URL + "/updateAdvertiser", payload, {
    headers: {
      Authorization: `${token}`,
    },
  });
};

export const deleteAdvertiser = (advertiserId, token) => {
  if (!advertiserId) {
    return Promise.reject(new Error("Advertiser ID is required"));
  }

  return axios.delete(process.env.REACT_APP_API_URL + "/deleteAdvertiser", {
    params: { advertiserId },
    headers: {
      Authorization: `${token}`,
    },
  });
};

export const createCategory = (name, token) => {
  return createAdvertiserCategory(name, token);
};

export const deleteCategory = (categoryId, token) => {
  return deleteAdvertiserCategory(categoryId, token);
};
